import { ReactElement, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconTableExport } from '@tabler/icons-react';
import { Button } from 'antd';
import { Page, CrudMode, notification, ExportAllModal, ExportEntityConfig } from '@ohpen/enso-ui';
import { useAuthz } from '@ohpen/react-authz';
import {
  ExportTemplatesList,
  ExportTemplatesCrudForm,
  ExportTemplatesDeleteModal,
  exportEntityTypes,
  filterActiveToday,
  flattenProductAdditionalFields,
  getColumnsSchema,
  exclusiveSetsColumns,
  baseRateColumns,
  historicColumns,
  ercColumns,
  periodColumns,
  feesColumns,
  bands,
} from '@pmp/components';
import {
  ActionEntityType,
  ExportTemplateResponse,
  FieldType,
  productsGetAllProducts,
  baseRatesGetAllBaseRates,
  ercProfilesGetAllErcProfiles,
  feeDefinitionsGetAllFeeDefinitions,
  feeProfilesGetAllFeeProfiles,
  productExclusivitySetsGetAllSets,
} from '@pmp/api';
import { ProductData } from '@pmp/types';
import { Permission } from '@pmp/contexts';
import { formatFileNameDateTime } from '@pmp/utils';

import { initialState, reducer } from './reducer';
import { useExportTemplatesStore, useValueListsStore } from '@pmp/stores';

const VALUE_LIST_NAMES: FieldType[] = [FieldType.ACTION_ENTITY_TYPE];

// Minimum permission to include an entity in the "Export all" modal. Exporting reads data, so the
// threshold is "at least view". Keyed by entity key.
const ENTITY_VIEW_PERMISSION: Record<string, Permission> = {
  products: 'products:view',
  baseRates: 'baseRates:view',
  ercProfiles: 'ercProfiles:view',
  feeDefinitions: 'fees:view',
  feeProfiles: 'feeProfiles:view',
  exclusives: 'exclusivitySets:view',
};

const currentYear = new Date().getFullYear();
const EXPORT_YEARS = { startYear: currentYear, endYear: currentYear + 1 };

export const ExportsPage = (): ReactElement => {
  const { valueLists, getValueLists } = useValueListsStore();
  const [data, setData] = useState<ExportTemplateResponse[]>([]);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();
  const { userPermissions } = useAuthz();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFileName, setExportFileName] = useState('');

  const { isLoadingExportTemplates, getAllExportTemplates, getExportTemplates } = useExportTemplatesStore();

  const fetchData = useCallback(() => {
    getAllExportTemplates()
      .then(data => {
        // TODO remove filter when BFF sends just the right templates
        setData(data.filter(item => exportEntityTypes.includes(item.entityType.key)));
      })
      .catch(error => {
        console.error(error);
        notification().error({ title: t('global.errors.failedToLoadExportTemplates') });
      });
  }, [getAllExportTemplates, t]);

  useEffect(() => {
    getValueLists({ fetchIfMissing: VALUE_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreate = useCallback((key?: string) => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE, entityType: key as ActionEntityType });
  }, []);

  const onClone = useCallback((record: ExportTemplateResponse) => {
    dispatch({
      type: 'openCrud',
      record: { ...record, id: '', name: '', description: '' },
      mode: CrudMode.CLONE,
      entityType: record.entityType.key,
    });
  }, []);

  const onView = useCallback((record: ExportTemplateResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW, entityType: record.entityType.key });
  }, []);

  const onEdit = useCallback((record: ExportTemplateResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT, entityType: record.entityType.key });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: ExportTemplateResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  const handleOpenExportModal = useCallback(() => {
    setExportFileName(`PMP export ${formatFileNameDateTime()}`);
    setExportModalOpen(true);
  }, []);

  const handleCloseExportModal = useCallback(() => {
    setExportModalOpen(false);
  }, []);

  const entities = useMemo(
    (): ExportEntityConfig[] => [
      {
        key: 'products',
        label: t('pmp.products.title'),
        columns: getColumnsSchema(ActionEntityType.PRODUCTS) as unknown as ExportEntityConfig['columns'],
        fetchRows: async activeFilters => {
          const res = await productsGetAllProducts({ query: EXPORT_YEARS });
          const rows = flattenProductAdditionalFields(res.data ?? []);
          return activeFilters.includes('activeToday')
            ? (filterActiveToday(rows) as unknown as Record<string, unknown>[])
            : (rows as unknown as Record<string, unknown>[]);
        },
        getTemplates: () => getExportTemplates(ActionEntityType.PRODUCTS),
        defaultSelected: true,
        filters: [{ key: 'activeToday', label: t('pmp.exports.onlyActiveToday'), defaultChecked: false }],
      },
      {
        key: 'baseRates',
        label: t('pmp.baseRates.title'),
        columns: baseRateColumns as unknown as ExportEntityConfig['columns'],
        expandedColumns: historicColumns as unknown as ExportEntityConfig['expandedColumns'],
        childKey: 'interestRates',
        flatten: true,
        fetchRows: async activeFilters => {
          const [baseRatesRes, productsRes] = await Promise.all([
            baseRatesGetAllBaseRates(),
            activeFilters.includes('onlyRelated') ? productsGetAllProducts({ query: EXPORT_YEARS }) : Promise.resolve(null),
          ]);
          let baseRates = baseRatesRes.data ?? [];
          if (activeFilters.includes('onlyRelated') && productsRes) {
            const activeTodayProducts = filterActiveToday((productsRes.data ?? []) as unknown as ProductData[]);
            const referencedCodes = new Set(
              activeTodayProducts.map(p => (p as unknown as { baseRate?: { key?: string } }).baseRate?.key).filter(Boolean),
            );
            baseRates = baseRates.filter(br => referencedCodes.has(br.code));
          }
          return baseRates as unknown as Record<string, unknown>[];
        },
        getTemplates: () => getExportTemplates(ActionEntityType.BASE_RATES),
        defaultSelected: true,
        linkedFilters: [
          {
            sourceEntityKey: 'products',
            sourceFilterKey: 'activeToday',
            key: 'onlyRelated',
            label: t('pmp.exports.onlyRelatedWithActiveToday'),
          },
        ],
      },
      {
        key: 'ercProfiles',
        label: t('pmp.erc.menuLabel'),
        columns: ercColumns as unknown as ExportEntityConfig['columns'],
        expandedColumns: periodColumns as unknown as ExportEntityConfig['expandedColumns'],
        childKey: 'penaltyTiers',
        flatten: true,
        fetchRows: async activeFilters => {
          const ercRes = await ercProfilesGetAllErcProfiles();
          let ercProfiles = ercRes.data ?? [];
          if (activeFilters.includes('onlyRelated')) {
            ercProfiles = ercProfiles.filter(e => e.linkedToActiveToday);
          }
          return ercProfiles as unknown as Record<string, unknown>[];
        },
        getTemplates: () => getExportTemplates(ActionEntityType.ERC_PROFILES),
        defaultSelected: true,
        linkedFilters: [
          {
            sourceEntityKey: 'products',
            sourceFilterKey: 'activeToday',
            key: 'onlyRelated',
            label: t('pmp.exports.onlyRelatedWithActiveToday'),
          },
        ],
      },
      {
        key: 'feeDefinitions',
        label: t('pmp.fees.labels.feeDefinitions'),
        columns: feesColumns as unknown as ExportEntityConfig['columns'],
        expandedColumns: bands as unknown as ExportEntityConfig['expandedColumns'],
        childKey: 'bands',
        flatten: true,
        fetchRows: async activeFilters => {
          const [feesRes, productsRes, feeProfilesRes] = await Promise.all([
            feeDefinitionsGetAllFeeDefinitions(),
            activeFilters.includes('onlyRelated') ? productsGetAllProducts({ query: EXPORT_YEARS }) : Promise.resolve(null),
            activeFilters.includes('onlyRelated') ? feeProfilesGetAllFeeProfiles() : Promise.resolve(null),
          ]);
          let fees = (feesRes.data ?? []).map(fee => ({
            ...fee,
            // Some payloads may expose the display name as `name` instead of `feeName`.
            // Keep `feeName` stable for export consumers (JSON and Excel).
            feeName: fee.feeName ?? (fee as unknown as { name?: string }).name,
          }));
          if (activeFilters.includes('onlyRelated') && productsRes && feeProfilesRes) {
            const activeTodayProducts = filterActiveToday((productsRes.data ?? []) as unknown as ProductData[]);
            const referencedFeeProfileIds = new Set(
              activeTodayProducts.map(p => (p as unknown as { feeProfile?: { key?: string } }).feeProfile?.key).filter(Boolean),
            );
            const referencedFeeProfiles = (feeProfilesRes.data ?? []).filter(fp => referencedFeeProfileIds.has(fp.feeProfileId));
            const referencedFeeIds = new Set(referencedFeeProfiles.flatMap(fp => fp.feeIds ?? []).filter(Boolean));
            fees = fees.filter(f => referencedFeeIds.has(f.feeId));
          }
          return fees as unknown as Record<string, unknown>[];
        },
        getTemplates: () => getExportTemplates(ActionEntityType.FEES),
        defaultSelected: true,
        linkedFilters: [
          {
            sourceEntityKey: 'products',
            sourceFilterKey: 'activeToday',
            key: 'onlyRelated',
            label: t('pmp.exports.onlyRelatedWithActiveToday'),
          },
        ],
      },
      {
        key: 'feeProfiles',
        label: t('pmp.fees.labels.feeProfiles'),
        columns: getColumnsSchema(ActionEntityType.FEE_PROFILES) as unknown as ExportEntityConfig['columns'],
        fetchRows: async activeFilters => {
          const [feeProfilesRes, productsRes] = await Promise.all([
            feeProfilesGetAllFeeProfiles(),
            activeFilters.includes('onlyRelated') ? productsGetAllProducts({ query: EXPORT_YEARS }) : Promise.resolve(null),
          ]);
          const feeProfiles = feeProfilesRes.data ?? [];
          if (activeFilters.includes('onlyRelated') && productsRes) {
            const activeTodayProducts = filterActiveToday((productsRes.data ?? []) as unknown as ProductData[]);
            const referencedIds = new Set(
              activeTodayProducts.map(p => (p as unknown as { feeProfile?: { key?: string } }).feeProfile?.key).filter(Boolean),
            );
            return feeProfiles.filter(fp => referencedIds.has(fp.feeProfileId)) as unknown as Record<string, unknown>[];
          }
          return feeProfiles as unknown as Record<string, unknown>[];
        },
        getTemplates: () => getExportTemplates(ActionEntityType.FEE_PROFILES),
        defaultSelected: true,
        linkedFilters: [
          {
            sourceEntityKey: 'products',
            sourceFilterKey: 'activeToday',
            key: 'onlyRelated',
            label: t('pmp.exports.onlyRelatedWithActiveToday'),
          },
        ],
      },
      {
        key: 'exclusives',
        label: t('pmp.exclusiveProducts.title'),
        columns: exclusiveSetsColumns as unknown as ExportEntityConfig['columns'],
        fetchRows: async activeFilters => {
          const [exclusivesRes, productsRes] = await Promise.all([
            productExclusivitySetsGetAllSets(),
            activeFilters.includes('onlyRelated') ? productsGetAllProducts({ query: EXPORT_YEARS }) : Promise.resolve(null),
          ]);
          const exclusives = exclusivesRes.data ?? [];
          if (activeFilters.includes('onlyRelated') && productsRes) {
            const activeTodayProducts = filterActiveToday((productsRes.data ?? []) as unknown as ProductData[]);
            const referencedIds = new Set(
              activeTodayProducts
                .flatMap(p => (p as unknown as { belongsToExclusivitySets?: string[] }).belongsToExclusivitySets ?? [])
                .filter(Boolean),
            );
            return exclusives.filter(e => referencedIds.has(e.id)) as unknown as Record<string, unknown>[];
          }
          return exclusives as unknown as Record<string, unknown>[];
        },
        getTemplates: () => getExportTemplates(ActionEntityType.EXCLUSIVITY_SETS),
        defaultSelected: true,
        linkedFilters: [
          {
            sourceEntityKey: 'products',
            sourceFilterKey: 'activeToday',
            key: 'onlyRelated',
            label: t('pmp.exports.onlyRelatedWithActiveToday'),
          },
        ],
      },
    ],
    [t, getExportTemplates],
  );

  // Only offer entities the user can at least view — exporting reads data, so it must respect the
  // same per-entity view permission as the rest of the app.
  const visibleEntities = useMemo(() => {
    return entities.filter(entity => {
      const needed = ENTITY_VIEW_PERMISSION[entity.key];
      return !needed || userPermissions.has(needed);
    });
  }, [entities, userPermissions]);

  return (
    <Page>
      <Page.Title>{t('global.menuLabels.exports')}</Page.Title>
      <Page.Actions>
        {visibleEntities.length > 0 && (
          <Button type="primary" icon={<IconTableExport size={16} strokeWidth={1.5} />} onClick={handleOpenExportModal}>
            {t('global.buttons.exportAll')}
          </Button>
        )}
      </Page.Actions>
      <Page.Content>
        <ExportTemplatesList
          isFetching={isLoadingExportTemplates}
          valueLists={valueLists}
          data={data ?? []}
          isCrudOpen={state.crud.open}
          onCreate={onCreate}
          onClone={onClone}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
        {state.crud?.open && state.crud?.mode && (
          <ExportTemplatesCrudForm
            entityType={state.crud.entityType}
            mode={state.crud.mode}
            record={state.crud.record}
            onClose={onCloseCrud}
            refreshTable={fetchData}
          />
        )}
      </Page.Content>
      <ExportTemplatesDeleteModal
        open={state.deleteModal.open}
        record={state.deleteModal.record}
        onClose={onCloseDeleteModal}
        refreshTable={fetchData}
      />

      <ExportAllModal
        open={exportModalOpen}
        onClose={handleCloseExportModal}
        fileName={exportFileName}
        editableFileName
        entities={visibleEntities}
        width={650}
      />
    </Page>
  );
};
